import { rankOf, suitOf, type Card } from '@big2/shared';

/**
 * Determines whether a card in the player's hand should start a new visual group,
 * introducing an elegant group separation gap (e.g. between suits or between pairs).
 */
export function isCardGroupStart(
  prevCard: Card | undefined,
  currentCard: Card,
  hand: readonly Card[],
  sortBy?: string,
  trumpSuit?: string
): boolean {
  if (!prevCard) return false;

  // 1. Grouping by color/suit: separate when suit changes
  if (sortBy === 'suit') {
    return suitOf(prevCard) !== suitOf(currentCard);
  }

  // 2. Grouping by pairs: separate between different pairs/triples, and between pairs and singles
  if (sortBy === 'pairs') {
    const rankCounts: Record<string, number> = {};
    for (const c of hand) {
      const r = rankOf(c);
      rankCounts[r] = (rankCounts[r] ?? 0) + 1;
    }

    const prevRank = rankOf(prevCard);
    const currRank = rankOf(currentCard);
    const prevIsPair = (rankCounts[prevRank] ?? 0) >= 2;
    const currIsPair = (rankCounts[currRank] ?? 0) >= 2;

    // Transition from one pair/triple to a different pair/triple
    if (prevIsPair && currIsPair && prevRank !== currRank) {
      return true;
    }

    // Transition from the pairs section to the singles section
    if (prevIsPair && !currIsPair) {
      return true;
    }

    return false;
  }

  // 3. Muushig trump vs non-trump separation
  if (trumpSuit) {
    const prevIsTrump = suitOf(prevCard) === trumpSuit;
    const currIsTrump = suitOf(currentCard) === trumpSuit;
    if (prevIsTrump !== currIsTrump) {
      return true;
    }
  }

  return false;
}
